import * as Dfns from 'date-fns/fp';
import * as R from 'ramda';
import { Dispatch, SetStateAction } from 'react';
import { Plover } from '@blisspointmedia/bpm-types/dist/webTrafficOptimization';
import { formValidator } from '../../Validation/wto-form-validator';
import { WtoDowOperation, WtoRule, WtoVariant } from '../../wto-form-context';
import { getCookieOperationUrls } from './CookieSection';

export const createValidateRule = (
  newRule: WtoRule,
  wtoFormObj: Plover,
  ruleIndex: number | null,
  showRuleCards: boolean[],
  setShowRuleCards: Dispatch<SetStateAction<boolean[]>>,
  setWtoFormObj: Dispatch<SetStateAction<Plover>>,
  setValidationErrors: Dispatch<SetStateAction<string[]>>,
  closeModal: () => void
) => {
  const validateRule = () => {
    // Plover.rules is only weakly typed (Record<string, any>[]) in bpm-types; every
    // rule read off wtoFormObj is actually WtoRule-shaped at runtime.
    const wtoRules = wtoFormObj.rules as WtoRule[];

    const validationArray = [
      'rule_name',
      'conditions',
      'variant_splits',
      'timeframe_dates',
      'timezone',
      'additional_utm',
      'cookie_operation',
      'cookie_operation_url',
      'dow_operation',
      'customRetention'
    ];
    if (newRule.timeframe && newRule.timeframe.timeframe_utm_test_id_enabled) {
      validationArray.push('timeframe_utm_test_id');
    }
    const errors = formValidator(
      newRule,
      validationArray,
      {
        isBulkPlover: false
      },
      {
        currentUtmTestId: wtoFormObj.utm_test_id,
        currentRulesArray: wtoRules,
        currentRuleIndex: Number(newRule.rule_order)
      }
    );
    const newRuleFormObj = R.clone(newRule);
    const validDows = newRuleFormObj.dow_operation.filter(
      (dowOp: WtoDowOperation) => dowOp.day.length > 0
    );
    newRuleFormObj.dow_operation = validDows;
    validDows.forEach((dowOperation, index) => {
      if (
        Dfns.isAfter(
          Dfns.parseISO(dowOperation.end_time),
          Dfns.parseISO(dowOperation.start_time)
        ) ||
        dowOperation.start_time === dowOperation.end_time
      ) {
        errors.push(
          `Day of the week ${index} start and end times cannot be the same and start time cannot be ahead of end time`
        );
      }
    });

    if (errors.length > 0) {
      setValidationErrors(errors);
    } else {
      const newWtoFormObj = R.clone(wtoFormObj);

      // helper function to insert a value at an index without mutating the original array
      const insertAt = <T>(array: T[], index: number, value: T): T[] => [
        ...array.slice(0, index),
        value,
        ...array.slice(index)
      ];

      // helper function to determine if a rule has custom retention enabled
      const isNotCustomRetentionRule = (rule: WtoRule) =>
        !rule.customRetention?.customRetentionStatus;

      // first simple rule
      const simpleFirstIndex = wtoRules.findIndex(
        rule =>
          isNotCustomRetentionRule(rule) &&
          !rule.timeframe_enabled &&
          rule.dow_operation.length === 0
      );

      // first dow rule
      const dowFirstIndex = wtoRules.findIndex(
        rule =>
          isNotCustomRetentionRule(rule) && !rule.timeframe_enabled && rule.dow_operation.length > 0
      );

      // last dow rule
      const dowLastIndex = wtoRules.findLastIndex(
        rule =>
          isNotCustomRetentionRule(rule) && !rule.timeframe_enabled && rule.dow_operation.length > 0
      );

      // first timeframe rule
      const timeframeFirstIndex = wtoRules.findIndex(
        rule => isNotCustomRetentionRule(rule) && rule.timeframe_enabled && rule.timeframe
      );

      // last timeframe rule
      const timeframeLastIndex = wtoRules.findLastIndex(
        rule => isNotCustomRetentionRule(rule) && rule.timeframe_enabled && rule.timeframe
      );

      // last custom retention rule
      const customRetentionLastIndex = wtoRules.findLastIndex(rule =>
        Boolean(rule.customRetention?.customRetentionStatus)
      );

      // update both the list of rules, and show/hide state of each rule card
      let newRules = R.clone(wtoRules);
      let newShowRuleCards = [...showRuleCards];

      // helper function to remove old data for a rule if it has been updated in a new index
      const removeOldRule = newIndex => {
        if (ruleIndex || ruleIndex === 0) {
          const removeAt = newIndex > ruleIndex ? ruleIndex : ruleIndex + 1;
          newRules = newRules.filter((_, i) => i !== removeAt);
          newShowRuleCards = newShowRuleCards.filter((_, i) => i !== removeAt);
        }
      };

      // if edited rule has custom retention enabled, place at top of list
      if (newRule.customRetention && newRule.customRetention.customRetentionStatus) {
        newRules = [newRule, ...newRules];
        newShowRuleCards = [true, ...showRuleCards];
        removeOldRule(0);

        // if edited rule has timeframe enabled:
        // - if a custom retention rule exists, insert after the last custom retention rule
        // - else if a dow rule exists, insert before first dow rule
        // - else if a simple rule exists, insert before first simple rule
        // - else, insert at the end of the list
      } else if (newRule.timeframe_enabled && newRule.timeframe) {
        let newTimeframeIndex = newRules.length;
        if (customRetentionLastIndex !== -1) {
          newTimeframeIndex = customRetentionLastIndex + 1;
        } else if (timeframeFirstIndex !== -1) {
          newTimeframeIndex = timeframeFirstIndex;
        } else if (dowFirstIndex !== -1) {
          newTimeframeIndex = dowFirstIndex;
        } else if (simpleFirstIndex !== -1) {
          newTimeframeIndex = simpleFirstIndex;
        }
        newRules = insertAt(newRules, newTimeframeIndex, newRule);
        newShowRuleCards = insertAt(newShowRuleCards, newTimeframeIndex, true);
        removeOldRule(newTimeframeIndex);

        // if edited rule has day of week operation:
        // - if a timeframe rule exists, insert after the last timeframe rule
        // - else if a simple rule exists, insert before first simple rule
        // - else, insert at the end of the list
      } else if (newRule.dow_operation.length > 0) {
        let newDowIndex = newRules.length;
        if (timeframeLastIndex !== -1) {
          newDowIndex = timeframeLastIndex + 1;
        } else if (dowFirstIndex !== -1) {
          newDowIndex = dowFirstIndex;
        } else if (simpleFirstIndex !== -1) {
          newDowIndex = simpleFirstIndex;
        }
        newRules = insertAt(newRules, newDowIndex, newRule);
        newShowRuleCards = insertAt(newShowRuleCards, newDowIndex, true);
        removeOldRule(newDowIndex);

        // if edited rule is simple (none of the preceeding cases apply):
        // - if editing existing rule, edit rule at current index
        // - else, insert rule at the end of the list
      } else if (ruleIndex || ruleIndex === 0) {
        const newSimpleIndex = ruleIndex > dowLastIndex ? ruleIndex : newRules.length;
        newRules = insertAt(newRules, newSimpleIndex, newRule);
        newShowRuleCards = insertAt(newShowRuleCards, newSimpleIndex, true);
        removeOldRule(newSimpleIndex);
      } else {
        newRules = [...newRules, newRule];
        newShowRuleCards = [...showRuleCards, true];
      }

      // update rule orders
      newRules = newRules.map((rule, orderIndex) => {
        const updatedRule = { ...rule, rule_order: `${orderIndex}` };

        // ensure all URLs are added if cookie operation(s) have been added
        // and cookie operation URL option has been left selected as "All" by default
        if (
          updatedRule.cookie_operation.length > 0 &&
          updatedRule.cookie_operation_url.length === 0
        ) {
          // Plover.variants is only weakly typed (Record<string, any>[]) in bpm-types; every
          // variant read off wtoFormObj is actually WtoVariant-shaped at runtime.
          updatedRule.cookie_operation_url = getCookieOperationUrls(
            updatedRule.variant_splits,
            wtoFormObj.variants as WtoVariant[]
          );
        }
        return updatedRule;
      });

      // save changes in state and close modal
      newWtoFormObj.rules = newRules;
      setShowRuleCards(newShowRuleCards);
      setWtoFormObj(newWtoFormObj);
      closeModal();
    }
  };

  return { validateRule };
};
